<script setup lang="ts">
import { NodeViewContent, nodeViewProps, NodeViewWrapper } from "@tiptap/vue-3";
import {
    MailMjmlSection,
    MailMjmlColumn,
    type MailBlockContentRegion,
} from "#components";
import { createSectionBlock } from "~/stores/mail-block-editor/defaults";

const props = defineProps(nodeViewProps);
const store = useMailStore();
const region = ref<typeof MailBlockContentRegion>();

const builder = useMailStore();
const theme = computed(() => {
    const theme = createSectionBlock();

    const topLeft =
        props.node.attrs.borderTopLeftRadius ?? theme?.borderTopLeftRadius;
    const bottomLeft =
        props.node.attrs.borderBottomLeftRadius ??
        theme?.borderBottomLeftRadius;
    const topRight =
        props.node.attrs.borderTopRightRadius ?? theme?.borderTopRightRadius;
    const bottomRight =
        props.node.attrs.borderBottomRightRadius ??
        theme?.borderBottomRightRadius;
    const borderColor = props.node.attrs.borderColor ?? "";

    return builder.fieldCollection.replaceContent({
        outer: {
            paddingBottom: props.node.attrs.marginBottom ?? theme?.marginBottom,
            paddingTop: props.node.attrs.marginTop ?? theme?.marginTop,
            backgroundUrl:
                props.node.attrs.backgroundImage ?? theme?.backgroundImage,
        },
        inner: {
            width: props.node.attrs.width,
            borderBottom: props.node.attrs.borderBottomWidth
                ? `${props.node.attrs.borderBottomWidth ?? ""} solid ${borderColor}`.trim()
                : undefined,
            borderLeft: props.node.attrs.borderLeftWidth
                ? `${props.node.attrs.borderLeftWidth ?? ""} solid ${borderColor}`.trim()
                : undefined,
            borderRight: props.node.attrs.borderRightWidth
                ? `${props.node.attrs.borderRightWidth ?? ""} solid ${borderColor}`.trim()
                : undefined,
            borderTop: props.node.attrs.borderTopWidth
                ? `${props.node.attrs.borderTopWidth ?? ""} solid ${borderColor}`.trim()
                : undefined,
            borderRadius: `${topLeft} ${topRight} ${bottomRight} ${bottomLeft}`,
            backgroundColor:
                props.node.attrs.backgroundColor ?? theme?.backgroundColor,
            paddingBottom:
                props.node.attrs.paddingBottom ?? theme?.paddingBottom,
            paddingLeft: props.node.attrs.paddingLeft ?? theme?.paddingLeft,
            paddingRight: props.node.attrs.paddingRight ?? theme?.paddingRight,
            paddingTop: props.node.attrs.paddingTop ?? theme?.paddingTop,
        },
    });
});

const section = ref(null);

watch(
    [() => props.node.attrs.marginBottom, () => props.node.attrs.marginTop],
    () => {
        region.value?.updatePosition();
    },
);

useResizeObserver(section, () => {
    region.value?.updatePosition();
});

function insertAbove() {
    store.insertAbove(props.node.attrs.id, "section");
}

function insertBelow() {
    store.insertBelow(props.node.attrs.id, "section");
}
</script>

<template>
    <NodeViewWrapper ref="section" class="section" @click.stop="store.focusedJsonId = node.attrs.id">
        <MailMjmlSection :css-class="node.attrs.identifier" :padding-bottom="theme.outer.paddingBottom as string"
            :padding-top="theme.outer.paddingTop as string" :background-url="theme.outer.backgroundUrl as string">
            <MailMjmlColumn :full-width="!theme.inner.width || theme.inner.width === '100%'" :width="theme.inner.width"
                :border-bottom="theme.inner.borderBottom as string" :border-left="theme.inner.borderLeft as string"
                :border-right="theme.inner.borderRight as string" :border-top="theme.inner.borderTop as string"
                :border-radius="theme.inner.borderRadius as string" :background-color="theme.inner.backgroundColor"
                :padding-bottom="theme.inner.paddingBottom as string" :padding-left="theme.inner.paddingLeft as string"
                :padding-right="theme.inner.paddingRight as string" :padding-top="theme.inner.paddingTop as string">
                <NodeViewContent as="div" class="w-full" />
            </MailMjmlColumn>
        </MailMjmlSection>
    </NodeViewWrapper>
</template>
